import AutoSizeText from "@/components/Common/AutoSizeText";
import InterestChip from "@/components/Common/InterestChip";
import clsxm from "@/lib/clsxm";
import {useCategoryStore} from "@/store/category";
import useRegistrationStore from "@/store/registration";
import type {FC} from "react";
import {useTranslation} from "react-i18next";
import {useShallow} from "zustand/shallow";

const InterestSelection: FC<{compact?: boolean; isLoading?: boolean; label?: string}> = ({
  compact,
  isLoading,
  label,
}) => {
  const {t} = useTranslation("common");
  const categories = useCategoryStore(state => state.categories);
  const maxAllowedCategories = useCategoryStore(state => state.maxAllowedCategories);
  const selectedInterests = useRegistrationStore(useShallow(state => state.interests ?? []));
  const selectInterest = useRegistrationStore(state => state.selectInterest);
  const canSelectMore = selectedInterests.length < maxAllowedCategories;

  return (
    <div
      className={clsxm("flex flex-col items-stretch gap-1 transition-opacity duration-150", isLoading && "opacity-50")}>
      <span className="text-neutral-grey-dark text-sm font-medium">
        {t((label as "selectInterestsLabel") ?? "selectInterestsLabel", {max_interests: maxAllowedCategories})}
      </span>
      <div
        className={
          compact
            ? "flex flex-wrap gap-x-1 gap-y-2"
            : "big:grid-cols-4 grid grid-cols-2 gap-4 p-1 sm:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4"
        }>
        {categories.map(category => {
          const isSelected = selectedInterests.includes(category.id);
          const isDisabled = isLoading || (!canSelectMore && !isSelected);

          if (compact)
            return (
              <InterestChip
                category={category}
                className={isDisabled ? "opacity-50" : undefined}
                dataTestId={`interest-category-${category.id}`}
                isDisabled={isDisabled}
                isSelected={isSelected}
                key={category.id}
                onClick={() => selectInterest(category.id)}
              />
            );

          return (
            <button
              data-testid={`interest-category-${category.id}`}
              data-selected={isSelected ? "true" : "false"}
              disabled={isDisabled}
              onClick={() => selectInterest(category.id)}
              key={category.id}
              type="button"
              className={clsxm(
                "relative aspect-square overflow-hidden rounded-md border-2 bg-transparent transition-all duration-150 disabled:cursor-not-allowed",
                !isLoading && "enabled:hover:drop-shadow-lg",
                isSelected ? "border-primary-dark-solid shadow-md" : "border-transparent",
              )}>
              <div className="relative h-full w-full">
                <img
                  src={(category.image640 ?? category.image)!}
                  loading="lazy"
                  alt=""
                  className="h-full w-full object-cover"
                />
                <div
                  className={
                    "absolute inset-0 z-10 transition-all duration-150 " +
                    (!canSelectMore && !isSelected
                      ? "bg-neutral-grey-light/50 backdrop-brightness-125"
                      : "backdrop-filter-none")
                  }
                />
              </div>
              <div className="absolute bottom-3 left-3 z-20 max-w-[80%] overflow-hidden">
                <div
                  className={
                    "relative flex items-center justify-center rounded-md px-2.5 py-2 " +
                    (category.name?.includes(" ") ? "w-min" : "")
                  }>
                  <div className="absolute inset-0 -z-10 overflow-hidden rounded-md bg-gray-500/10 backdrop-blur-xs" />
                  <div className="absolute inset-0 -z-10 overflow-hidden rounded-md bg-gray-500/10 backdrop-blur-xs" />
                  <div className="absolute inset-0 -z-10 overflow-hidden rounded-md border border-white bg-gray-500/10 backdrop-blur-xs" />
                  <AutoSizeText
                    className="z-20 grow-0 items-center text-left font-medium text-white"
                    fontSizeMin={8}
                    maxLines={2}
                    lineHeight={1}
                    fontSizeMax={14}>
                    {category.name}
                  </AutoSizeText>
                </div>
              </div>

              <div
                className={
                  "absolute top-4 right-4 z-10 flex size-5 items-center justify-center rounded-full border bg-white p-px shadow-xs transition-all duration-150 " +
                  (isSelected ? "border-primary-dark-solid" : "border-primary-light-solid")
                }>
                <div
                  className={
                    "size-4 rounded-full transition-all duration-150 " +
                    (isSelected ? "bg-primary-dark-solid" : "bg-white")
                  }
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InterestSelection;
